import type { CSSVariablesResolver } from "@mantine/core";
import { SA_SHADOWS } from "../tokens/effects";

/* Tokens do Claude Design (docs/design-system/tokens/*.css) com os MESMOS nomes
   `--sa-*`: um valor do protótipo vira código sem tradução, e ninguém precisa
   arredondar "D900 a 10%" para o token sólido mais parecido (era isso que
   devolvia a cara antiga às telas novas).

   O que depende do esquema de cor usa as virtual colors do tema e troca sozinho
   no escuro; o que é alfa sobre o texto (bordas, hovers, texto esmaecido) tem
   valor próprio por esquema, copiado do DS. Onde o DS não define o escuro
   (fill-neutral-strong, brand-wash, text-accent), o valor segue a mesma regra
   do resto do bloco escuro. */

const INK_LIGHT = "5, 4, 30"; // D900
const INK_DARK = "234, 234, 234"; // L300
const BRAND = "103, 52, 255"; // R500

const PALETTE = [
  "L100",
  "L200",
  "L300",
  "L500",
  "L600",
  "L700",
  "L750",
  "L800",
  "L900",
  "D300",
  "D400",
  "D450",
  "D500",
  "D600",
  "D700",
  "D900",
  "P900",
  "R300",
  "R500",
  "R600",
  "R700",
  "V300",
  "V500",
  "V700",
  "A300",
  "A500",
  "A700",
  "Y300",
  "Y700",
];

// `--sa-L100` etc.: o DS escreve a paleta com esse prefixo.
const paletteVariables = Object.fromEntries(
  PALETTE.map((name) => [`--sa-${name}`, `var(--mantine-color-${name}-0)`])
);

export const superAgentsCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    ...paletteVariables,

    // Superfícies (virtual colors: trocam no escuro)
    "--sa-surface-app": "var(--mantine-color-background_1-0)",
    "--sa-surface-sidebar": "var(--mantine-color-background_2-0)",
    "--sa-surface-panel": "var(--mantine-color-background_3-0)",
    "--sa-surface-subtle": "var(--mantine-color-background_4-0)",
    "--sa-surface-tooltip": "var(--mantine-color-P900-0)",
    "--sa-surface-auth": "#F6F2FF",

    // Texto sólido
    "--sa-text": "var(--mantine-color-plain_text-0)",
    "--sa-text-on-brand": "var(--mantine-color-text_with_colorful_bg-0)",
    "--sa-text-disabled": "var(--mantine-color-disabled_input_text-0)",

    // Marca
    "--sa-brand": "var(--mantine-color-R500-0)",
    "--sa-brand-hover": "var(--mantine-color-R600-0)",
    "--sa-brand-deep": "var(--mantine-color-R700-0)",
    "--sa-brand-tint-weak": `rgba(${BRAND}, 0.08)`,
    "--sa-brand-tint-strong": `rgba(${BRAND}, 0.16)`,
    "--sa-brand-tint-border": `rgba(${BRAND}, 0.18)`,
    /* R500 a 14%: o fundo "ativo" do DS (item ativo do menu e da aba, chip do
       setor) e o avatar da prévia do bloqueio de recurso. */
    "--sa-brand-tint-active": `rgba(${BRAND}, 0.14)`,
    /* Bloco de destaque interno (readme do DS › Cards: "fundo R500 7–9%, raio
       12"): o 9% é o do bloco "O que essa funcionalidade faz" do FeatureGate. */
    "--sa-brand-tint-block": `rgba(${BRAND}, 0.09)`,

    /* Véu sobre a tela desfocada do bloqueio de recurso (readme › Superfícies:
       "o único gradiente é o véu branco (40%→80%)"). Feito da superfície do
       painel para virar véu escuro no tema escuro, em vez de clarear o D900. */
    "--sa-gate-veil": `linear-gradient(180deg, color-mix(in srgb, var(--sa-surface-panel) 40%, transparent), color-mix(in srgb, var(--sa-surface-panel) 80%, transparent))`,

    // Semânticas
    "--sa-danger": "var(--mantine-color-V500-0)",
    "--sa-danger-hover": "var(--mantine-color-V700-0)",
    /* Hover do botão destrutivo (fundo V500). O DS escreve V700, mas V500→V700
       escurece o dobro do R500→R600 do primário e o botão "apagava" no mouse.
       Sem V600 na paleta, o meio-termo dá o mesmo passo do primário (~25% mais
       escuro). `--sa-danger-hover` segue V700 porque é cor de texto (Banner). */
    "--sa-danger-fill-hover":
      "color-mix(in srgb, var(--mantine-color-V500-0) 54%, var(--mantine-color-V700-0))",
    "--sa-danger-tint": "rgba(245, 69, 46, 0.2)",
    "--sa-success": "var(--mantine-color-A500-0)",
    "--sa-success-ink": "var(--mantine-color-A700-0)",
    "--sa-success-toast": "rgba(194, 214, 0, 0.95)",
    "--sa-success-border": "rgba(78, 86, 0, 0.25)",
    /* Aviso de atenção (Banner `attention`, área sem pessoas, SA-1367): fundo
       A500 a 18%, como no protótipo. Tinta e borda trocam no escuro. */
    "--sa-attention-surface":
      "color-mix(in srgb, var(--mantine-color-A500-0) 18%, transparent)",

    // SA-1426 (Configurações e respostas rápidas): tints de marca do protótipo.
    "--sa-brand-tint-nav": `rgba(${BRAND}, 0.10)`,
    "--sa-brand-tint-banner": `rgba(${BRAND}, 0.07)`,
    "--sa-brand-tint-row": `rgba(${BRAND}, 0.06)`,
    "--sa-brand-tint-faint": `rgba(${BRAND}, 0.05)`,
    "--sa-brand-border-hover": `rgba(${BRAND}, 0.45)`,
    "--sa-danger-tint-weak":
      "color-mix(in srgb, var(--sa-danger) 10%, transparent)",

    // Status de conversa
    "--sa-status-attending": `rgba(${BRAND}, 0.85)`,
    "--sa-status-waiting": "rgba(245, 69, 46, 0.85)",
    "--sa-status-ai": "rgba(120, 132, 0, 0.85)",
    "--sa-status-closed": `rgba(${INK_LIGHT}, 0.45)`,

    // Raios
    "--sa-radius-xs": "2px",
    "--sa-radius-sm": "4px",
    "--sa-radius-nav": "6px",
    "--sa-radius-md": "8px",
    "--sa-radius-icon": "10px",
    "--sa-radius-lg": "12px",
    "--sa-radius-dialog": "14px",
    "--sa-radius-sheet": "16px",
    "--sa-radius-pill": "999px",

    // Sombras: só em camadas flutuantes, sempre D900 em alfa
    "--sa-shadow-tooltip": SA_SHADOWS.tooltip,
    "--sa-shadow-toast": SA_SHADOWS.toast,
    "--sa-shadow-popover": SA_SHADOWS.popover,
    "--sa-shadow-popover-lg": SA_SHADOWS.popoverLg,
    "--sa-shadow-menu": SA_SHADOWS.menu,
    "--sa-shadow-dialog": SA_SHADOWS.dialog,
    "--sa-shadow-sheet": SA_SHADOWS.sheet,
    "--sa-shadow-knob": SA_SHADOWS.knob,

    // Movimento
    "--sa-ease": "ease",
    "--sa-dur-fast": "140ms",
    "--sa-dur-pop": "160ms",
    "--sa-dur-base": "180ms",
    "--sa-dur-sheet": "200ms",
    "--sa-blur-preview": "3.5px",

    /* Espaçamento do DS. Não é a escala `spacing` do Mantine (lá xs = 10,
       sm = 12, lg = 20): `gap="xs"` não é o `--sa-space-xs`. */
    "--sa-space-xs": "4px",
    "--sa-space-sm": "8px",
    "--sa-space-md": "16px",
    "--sa-space-lg": "24px",
    "--sa-space-xl": "32px",
    "--sa-space-xxl": "48px",

    // Tipografia
    "--sa-font-body": "var(--mantine-font-family)",
    "--sa-font-heading": "var(--mantine-font-family-headings)",
    "--sa-font-mono": "var(--mantine-font-family-monospace)",
    "--sa-fs-xs": "var(--mantine-font-size-xs)",
    "--sa-fs-sm": "var(--mantine-font-size-sm)",
    "--sa-fs-md": "var(--mantine-font-size-md)",
    "--sa-fs-lg": "var(--mantine-font-size-lg)",
    "--sa-fs-xl": "var(--mantine-font-size-xl)",
    "--sa-fs-h1": "var(--mantine-h1-font-size)",
    "--sa-fs-h2": "var(--mantine-h2-font-size)",
    "--sa-fs-h3": "var(--mantine-h3-font-size)",
    "--sa-fs-h4": "var(--mantine-h4-font-size)",
    "--sa-fs-h5": "var(--mantine-h5-font-size)",
    "--sa-fs-h6": "var(--mantine-h6-font-size)",
    "--sa-fw-regular": "400",
    "--sa-fw-semibold": "600",
    "--sa-fw-bold": "700",
    // Tamanhos intermediários que a escala do Mantine não tem
    "--sa-fs-meta": "12.5px",
    "--sa-fs-row": "13px",
    "--sa-fs-tab": "13.5px",
    "--sa-fs-name": "15px",
    "--sa-fs-chat-name": "17px",
    "--sa-fs-dialog-title": "22px",
    "--sa-fs-page-title": "26px",
    // Tag do plano mínimo no menu (NavItem `planTag` do DS): 10px, tracking 0.5.
    "--sa-fs-plan-tag": "10px",
    "--sa-plan-tag-tracking": "0.5px",
    "--sa-fw-heavy": "800",
    "--sa-lh-tight": "1.25",
    "--sa-lh-body": "1.55",
    "--sa-eyebrow-size": "11px",
    "--sa-eyebrow-tracking": "0.6px",

    // Medidas de layout
    "--sa-sidebar-width": "268px",
    "--sa-sidebar-rail": "64px",
    "--sa-chat-header-height": "80px",
    "--sa-dialog-width": "540px",
    "--sa-gate-width": "480px",
  },
  light: {
    "--sa-text-muted": `rgba(${INK_LIGHT}, 0.6)`,
    "--sa-text-subtle": `rgba(${INK_LIGHT}, 0.45)`,
    "--sa-divider": `rgba(${INK_LIGHT}, 0.08)`,
    "--sa-border": `rgba(${INK_LIGHT}, 0.10)`,
    "--sa-border-strong": `rgba(${INK_LIGHT}, 0.16)`,
    "--sa-hover": `rgba(${INK_LIGHT}, 0.05)`,
    "--sa-fill-neutral": `rgba(${INK_LIGHT}, 0.08)`,
    "--sa-fill-neutral-strong": `rgba(${INK_LIGHT}, 0.22)`,
    // D900 a 7%: moldura de ícone neutra e divisor fraco (FeatureGate).
    "--sa-fill-neutral-weak": `rgba(${INK_LIGHT}, 0.07)`,
    "--sa-border-input": `rgba(${INK_LIGHT}, 0.14)`,
    "--sa-border-keycap": `rgba(${INK_LIGHT}, 0.18)`,
    "--sa-border-dropzone": `rgba(${INK_LIGHT}, 0.20)`,
    "--sa-hover-faint": `rgba(${INK_LIGHT}, 0.025)`,
    /* Ícone de atenção verde-limão (área sem pessoas, SA-1367): o protótipo
       escreve #7A8500, entre A700 e A500, fora da paleta. 3,4:1 sobre
       background_1 e 4:1 sobre branco (ícone pede 3:1). */
    /* Texto de erro pequeno: o V500 dá 3,6:1 no branco (reprova o AA abaixo de
       18px); o V700 passa com folga. */
    "--sa-danger-text": "var(--mantine-color-V700-0)",
    "--sa-attention-icon": "#7A8500",
    // Protótipo: título e ícone em A700, borda A700 a 24% (7,2:1 no fundo).
    "--sa-attention-ink": "var(--mantine-color-A700-0)",
    "--sa-attention-border":
      "color-mix(in srgb, var(--mantine-color-A700-0) 24%, transparent)",
    "--sa-brand-tint": `rgba(${BRAND}, 0.12)`,
    "--sa-brand-wash": "#EDE6FF",
    "--sa-text-accent": "var(--mantine-color-R500-0)",
    /* Texto da tag do plano no menu. O DS pede R500, que sobre a tag (R500
       12%) na sidebar dá 3,8:1 para 10px: R600 é o degrau que passa (5,7:1). */
    "--sa-plan-tag-ink": "var(--mantine-color-R600-0)",
    /* O `dimmed` do Mantine é gray-6 (#868e96): cinza frio de fora da marca,
       3,3:1 sobre branco. O DS pede "nunca cinza puro para texto"; com o texto
       esmaecido do DS (D900 a 60%) as ~680 chamadas de `c="dimmed"` passam
       a 5,4:1 sobre branco (5,1:1 sobre background_1) e ganham o tom da marca
       sem tocar em nenhuma delas. */
    "--mantine-color-dimmed": "var(--sa-text-muted)",
  },
  dark: {
    "--sa-text-muted": `rgba(${INK_DARK}, 0.65)`,
    "--sa-text-subtle": `rgba(${INK_DARK}, 0.45)`,
    "--sa-divider": `rgba(${INK_DARK}, 0.08)`,
    "--sa-border": `rgba(${INK_DARK}, 0.12)`,
    "--sa-border-strong": `rgba(${INK_DARK}, 0.18)`,
    "--sa-hover": `rgba(${INK_DARK}, 0.06)`,
    "--sa-fill-neutral": `rgba(${INK_DARK}, 0.10)`,
    "--sa-fill-neutral-strong": `rgba(${INK_DARK}, 0.22)`,
    // O DS não define o escuro: sobe um pouco, como hover e fill-neutral.
    "--sa-fill-neutral-weak": `rgba(${INK_DARK}, 0.08)`,
    // O DS não define o escuro: mesmo degrau do `--sa-border` e do `--sa-hover`, um pouco acima.
    "--sa-border-input": `rgba(${INK_DARK}, 0.16)`,
    "--sa-border-keycap": `rgba(${INK_DARK}, 0.20)`,
    "--sa-border-dropzone": `rgba(${INK_DARK}, 0.22)`,
    "--sa-hover-faint": `rgba(${INK_DARK}, 0.04)`,
    // O DS não define o escuro: o A500 (o limão claro) é o que lê sobre D500.
    /* No escuro o V700 some (1,9:1 sobre D900); o V500 lê (5,5:1). */
    "--sa-danger-text": "var(--mantine-color-V500-0)",
    "--sa-attention-icon": "var(--mantine-color-A500-0)",
    /* O DS não define o escuro: A700 some sobre D900; A300 lê (10:1) e a
       borda passa para o limão claro. */
    "--sa-attention-ink": "var(--mantine-color-A300-0)",
    "--sa-attention-border":
      "color-mix(in srgb, var(--mantine-color-A500-0) 24%, transparent)",
    "--sa-brand-tint": `rgba(${BRAND}, 0.4)`,
    "--sa-brand-wash": "#251A3D",
    /* O DS não define o escuro: R500 sobre D900 (ou sobre um tint de R500) fica
       em ~3:1. Mesma troca do `link_text`: R300 no escuro. */
    "--sa-text-accent": "var(--mantine-color-R300-0)",
    // Mesma troca do `--sa-text-accent`: R300 sobre o tint no escuro.
    "--sa-plan-tag-ink": "var(--mantine-color-R300-0)",
    "--mantine-color-dimmed": "var(--sa-text-muted)",
  },
});
